import { useState, MouseEvent, useMemo, useEffect } from 'react';
import { Box, IconButton, Menu, MenuItem } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import * as styles from './styles';
import * as localState from '../../state';
import { SortOption } from '@/types';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {
  PointOfInterestTemplateTypeEnum,
  TemplateTypeEnum,
  usePaintBlockTemplateQuery,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { TOTAL_AREA_FIELD_FORMATTER } from '../../constants';
import useFetchTemplatesByType from '@/hooks/useFetchTemplatesByType';
import { useAtom } from 'jotai';

const SORTING_ICON_MAP = new Map<undefined | number, React.ReactNode>([
  [undefined, <SwapVertIcon />],
  [-1, <ArrowUpwardIcon />],
  [1, <ArrowDownwardIcon />],
]);

const getNextSortDir = (currentSortDir: number | undefined): undefined | 1 | -1 => {
  if (currentSortDir === 1) return undefined;
  if (currentSortDir === -1) return 1;
  return -1;
};

export const CustomBlockSortingHeader = () => {
  const structureId = useGetStructureId();

  const [customBlocksQueryInput, setCustomBlocksQueryInput] = useAtom(
    localState.customBlocksQueryInput
  );

  const { data, loading, error } = usePaintBlockTemplateQuery({
    variables: {
      structureId,
      type: TemplateTypeEnum.PaintBlocks,
    },
    fetchPolicy: 'cache-first',
  });

  const {
    data: poiTemplatesData,
    loading: poiTemplatesLoading,
    error: poiTemplatesError,
  } = useFetchTemplatesByType();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  // Simplified sort options creation with standardized structure
  const sortOptions: SortOption[] = useMemo(() => {
    const paintBlockFields = data?.allTemplates?.at(0)?.fields || [];
    const poiTemplates = poiTemplatesData?.allPointOfInterestTemplate || [];

    // Create standardized sort options from paint block fields
    // Use field.key as id (for API sorting) and field.name as label (for display)
    const paintBlockOptions: SortOption[] = paintBlockFields.map((field) => ({
      id: field.name,
      label: field.name,
      type: 'paintBlock' as const,
    }));

    // Create standardized sort options from point of interest templates
    const poiOptions: SortOption[] = poiTemplates
      .filter(
        // Exlucde PaintBlockVolumeOfInterest i.e Workscopes, but keep POIs and VOIs
        (template) => template.type !== PointOfInterestTemplateTypeEnum.PaintBlockVolumeOfInterest
      )
      .map((template) => ({
        id: template.key || template.name, // Use key for API, fallback to name
        label: template.name,
        type: 'poi' as const,
      }));

    return [...paintBlockOptions, ...poiOptions];
  }, [data?.allTemplates, poiTemplatesData?.allPointOfInterestTemplate]);

  useEffect(() => {
    if (sortOptions && sortOptions.length > 0) {
      const initial =
        sortOptions.find((item) => item.id === 'totalArea')?.id || sortOptions?.[0]?.id;
      setCustomBlocksQueryInput((prev) => {
        if (prev?.sortBy) return prev;
        return {
          ...prev,
          sortBy: initial,
        };
      });
    }
  }, [sortOptions, setCustomBlocksQueryInput]);

  const handleSortClick = (event: MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleSortingButtonClick = () => {
    setCustomBlocksQueryInput((previous) => {
      return {
        ...previous,
        sortDir: getNextSortDir(previous.sortDir),
      };
    });
  };

  const handleSortOptionSelect = (sortId: string): void => {
    setCustomBlocksQueryInput((prev) => ({
      ...prev,
      sortBy: sortId,
    }));
    setAnchorEl(null);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  if (!sortOptions || sortOptions.length === 0) return <></>;

  const currentSortOption =
    sortOptions.find((option) => option.id === customBlocksQueryInput.sortBy) || sortOptions[0];

  if (loading || poiTemplatesLoading) return <></>;

  if (error || poiTemplatesError)
    return <p>Error loading templates: {error?.message || poiTemplatesError?.message}</p>;

  return (
    <styles.FilterBar>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <IconButton size="small" onClick={handleSortingButtonClick}>
          {SORTING_ICON_MAP.get(customBlocksQueryInput.sortDir)}
        </IconButton>
        <styles.SortButton
          sx={{ fontSize: '12px', fontWeight: 400 }}
          onClick={handleSortClick}
          endIcon={<KeyboardArrowDownIcon />}
          aria-controls={open ? 'sort-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          {TOTAL_AREA_FIELD_FORMATTER[currentSortOption?.label] || currentSortOption?.label} \ Total
          surface
        </styles.SortButton>

        <Menu
          id="sort-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'sort-button',
          }}
        >
          {sortOptions.map((option) => (
            <MenuItem
              key={option.id}
              onClick={() => handleSortOptionSelect(option.id)}
              selected={option.id === customBlocksQueryInput.sortBy}
            >
              {TOTAL_AREA_FIELD_FORMATTER[option.label] || option.label}
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </styles.FilterBar>
  );
};
